import React, { useState, useMemo } from "react";
import { useGetFinancialsQuery, useRefreshFinancials } from "apis/financial";
import { useGetCompaniesQuery } from "apis/company";
import { useGetUserQuery } from "apis/user";
import { ChevronRight, ChevronDown, Download, Loader2, RefreshCw } from "lucide-react";
import * as XLSX from "xlsx";
import Table, { buildExportRows } from "components/ui/Table";
import Filter from "components/financial/Filter";
import Payments from "components/financial/Payments";
import { formatDate, formatCurrency } from "utils/helperFunctions";

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

const formatNumber = (n) => new Intl.NumberFormat("en-AU").format(n || 0);
const dash = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const DOT = {
  purple: "bg-purple-500",
  blue: "bg-blue-500",
  green: "bg-green-500",
  amber: "bg-amber-500",
  orange: "bg-orange-400",
  teal: "bg-teal-500",
  indigo: "bg-indigo-500",
  pink: "bg-pink-500",
  cyan: "bg-cyan-500",
  violet: "bg-violet-500",
  rose: "bg-rose-500",
  lime: "bg-lime-500",
  gray: "bg-gray-400",
};
const Dot = ({ color }) => (
  <span
    className={`inline-block w-2 h-2 rounded-full ${DOT[color] || DOT.gray}`}
  />
);

const Money = ({ value, bold }) => (
  <span
    className={`tabular-nums ${bold ? "font-semibold text-black" : "text-darkergray"}`}
  >
    {formatCurrency(value)}
  </span>
);

const CategoryPill = ({ value }) => {
  if (!value) return <span className="text-darkgray">—</span>;
  const colorMap = {
    companySubscription: "bg-purple-100 text-purple-800 border-purple-200",
    beleef: "bg-blue-100 text-blue-800 border-blue-200",
    social: "bg-green-100 text-green-800 border-green-200",
    sms: "bg-cyan-100 text-cyan-800 border-cyan-200",
    photo: "bg-violet-100 text-violet-800 border-violet-200",
    editorReview: "bg-rose-100 text-rose-800 border-rose-200",
    photoService: "bg-teal-100 text-teal-800 border-teal-200",
    campaign: "bg-amber-100 text-amber-800 border-amber-200",
    priceProcess: "bg-orange-100 text-orange-800 border-orange-200",
    editorReview: "bg-indigo-100 text-indigo-800 border-indigo-200",
    letters: "bg-red-100 text-red-800 border-red-200",
    shop: "bg-rose-100 text-rose-800 border-rose-200",
    fruitBox: "bg-lime-100 text-lime-800 border-lime-200",
  };
  const cls = colorMap[value] || "bg-lightgray text-darkgray border-mediumgray";
  const labelMap = {
    photoService: "Photo Edit",
    editorReview: "Editor Review",
    priceProcess: "Price Process",
    companySubscription: "Company",
    fruitBox: "Fruit Box",
  };
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs border capitalize ${cls}`}
    >
      {labelMap[value] || value}
    </span>
  );
};

const MethodPill = ({ value }) => {
  const isNow = value === "payNow";
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
        isNow ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
      }`}
    >
      {isNow ? "Pay Now" : "Pay Later"}
    </span>
  );
};

const StatusPill = ({ value }) => {
  const map = {
    active: "bg-green-100 text-green-800",
    trialing: "bg-yellow-100 text-yellow-800",
  };
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
        map[value] || "bg-gray-100 text-gray-800"
      }`}
    >
      {value || "—"}
    </span>
  );
};

/* -------------------------------------------------------------------------- */
/*  Summary line primitives                                                   */
/* -------------------------------------------------------------------------- */

// A summary row: dot + name + meta on the left, one or two amounts on the right.
const SummaryRow = ({ dot, name, meta, indent, muted, right, rightAlt }) => (
  <div
    className={`flex items-center justify-between py-1.5 ${indent ? "pl-6" : ""}`}
  >
    <span className="flex items-center gap-2 text-sm">
      {dot ? <Dot color={dot} /> : indent ? null : <span className="w-2" />}
      <span className={muted ? "text-darkgray" : "text-black font-medium"}>
        {name}
      </span>
      {meta && <span className="text-xs text-darkgray">{meta}</span>}
    </span>
    <span className="flex items-center gap-8">
      {rightAlt !== undefined && (
        <span className="tabular-nums text-sm text-darkgray min-w-[90px] text-right">
          {rightAlt}
        </span>
      )}
      <span className="tabular-nums text-sm min-w-[90px] text-right">
        {right}
      </span>
    </span>
  </div>
);

const SummaryWrap = ({ label, children }) => (
  <div className="bg-white rounded-lg border border-mediumgray p-5 mb-4">
    {label && (
      <p className="text-xs font-semibold uppercase tracking-wider text-darkgray mb-3">
        {label}
      </p>
    )}
    {children}
  </div>
);

const TotalLine = ({
  label,
  sub,
  amount,
  amountAlt,
  subAmount,
  subAmountAlt,
}) => (
  <div className="border-t border-mediumgray mt-2 pt-3">
    <div className="flex items-center justify-between">
      <span className="font-semibold text-black">
        {label}{" "}
        {sub && (
          <span className="text-xs font-normal text-darkgray">{sub}</span>
        )}
      </span>
      <span className="flex items-center gap-8">
        {amountAlt !== undefined && (
          <span className="tabular-nums font-semibold text-black min-w-[90px] text-right">
            {amountAlt}
          </span>
        )}
        <span className="tabular-nums font-bold text-black min-w-[90px] text-right">
          {amount}
        </span>
      </span>
    </div>
    {(subAmount !== undefined || subAmountAlt !== undefined) && (
      <div className="flex items-center justify-between mt-1">
        <span className="text-xs text-darkgray">GST included (10%)</span>
        <span className="flex items-center gap-8">
          {subAmountAlt !== undefined && (
            <span className="tabular-nums text-xs text-darkgray min-w-[90px] text-right">
              {subAmountAlt}
            </span>
          )}
          <span className="tabular-nums text-xs text-darkgray min-w-[90px] text-right">
            {subAmount}
          </span>
        </span>
      </div>
    )}
  </div>
);

/* -------------------------------------------------------------------------- */
/*  Per-stream summary bodies                                                 */
/* -------------------------------------------------------------------------- */

const SubscriptionSummary = ({ data }) => {
  if (!data) return null;
  return (
    <SummaryWrap label="Summary">
      {(data.plans || []).map((p) => (
        <SummaryRow
          key={p.name}
          dot={p.dot}
          name={p.name}
          meta={`${formatCurrency(p.price)} / ${p.count} ${p.unit}`}
          right={<Money value={p.amount} />}
        />
      ))}
      <TotalLine
        label="Total"
        sub="(inc. GST)"
        amount={formatCurrency(data.totalIncGst)}
        subAmount={formatCurrency(data.gstIncluded)}
      />
    </SummaryWrap>
  );
};

const CampaignFeeSummary = ({ data }) => {
  if (!data) return null;
  return (
    <SummaryWrap label="Summary">
      {(data.rows || []).map((r) => (
        <SummaryRow
          key={r.name}
          dot={r.dot}
          name={r.name}
          meta={`${r.count} campaign fees`}
          right={<Money value={r.amount} />}
        />
      ))}
      <SummaryRow
        indent
        muted
        name="Base Price"
        right={<Money value={data.basePrice} />}
      />
      <SummaryRow indent muted name="GST" right={<Money value={data.gst} />} />
      <TotalLine
        label="Total"
        sub="(inc. GST)"
        amount={formatCurrency(data.totalIncGst)}
      />
    </SummaryWrap>
  );
};

const ServiceSummary = ({ data }) => {
  if (!data) return null;
  // Keep the decimal — Math.round would show the 4.4% rate as "4%"
  const ratePct = parseFloat(((data.rate || 0) * 100).toFixed(1));
  return (
    <SummaryWrap label={`Summary · ${ratePct}% Commission`}>
      <div className="flex items-center justify-end gap-8 pb-2 border-b border-mediumgray">
        <span className="text-xs text-darkgray uppercase tracking-wider min-w-[90px] text-right">
          Total
        </span>
        <span className="text-xs text-darkgray uppercase tracking-wider min-w-[90px] text-right">
          {ratePct}%
        </span>
      </div>
      {(data.methods || []).map((m) => (
        <div key={m.key} className="pt-2">
          <SummaryRow
            dot={m.dot}
            name={m.name}
            meta={`${m.orders} orders`}
            right=""
          />
          {(m.categories || []).map((c) => (
            <SummaryRow
              key={c.name}
              indent
              muted
              name={c.name}
              meta={`${c.orders} order${c.orders === 1 ? "" : "s"} · ${formatNumber(
                c.qty,
              )} qty`}
              rightAlt={formatCurrency(c.total)}
              right={formatCurrency(c.commission)}
            />
          ))}
          <div className="flex items-center justify-end gap-8 py-2 border-t border-mediumgray mt-1">
            <span className="tabular-nums text-sm text-darkgray min-w-[90px] text-right">
              {formatCurrency(m.subtotalTotal)}
            </span>
            <span
              className={`tabular-nums text-sm font-semibold min-w-[90px] text-right ${
                m.key === "payNow" ? "text-green-700" : "text-amber-600"
              }`}
            >
              {formatCurrency(m.subtotalCommission)}
            </span>
          </div>
        </div>
      ))}
      <TotalLine
        label="Total"
        sub="(inc. GST)"
        amount={formatCurrency(data.grandCommission)}
        amountAlt={formatCurrency(data.grandTotal)}
      />
      <div className="flex items-center justify-between mt-1">
        <span className="text-xs text-darkgray">
          GST included in commission (10%)
        </span>
        <span className="flex items-center gap-8">
          <span className="tabular-nums text-xs text-darkgray min-w-[90px] text-right">
            {formatCurrency(data.gstInTotal)}
          </span>
          <span className="tabular-nums text-xs text-darkgray min-w-[90px] text-right">
            {formatCurrency(data.gstInCommission)}
          </span>
        </span>
      </div>
    </SummaryWrap>
  );
};

const OtherSummary = ({ data }) => {
  if (!data) return null;
  return (
    <SummaryWrap label="Summary">
      {(data.groups || []).map((g) => (
        <div key={g.key} className="pt-2 first:pt-0">
          <SummaryRow
            dot={g.dot}
            name={g.name}
            meta={`${g.purchases} purchase${g.purchases === 1 ? "" : "s"}`}
            right={<Money value={g.amount} />}
          />
          {g.key === "campaign" ? (
            <SummaryRow
              indent
              muted
              name="Unit price"
              right={`${formatCurrency(g.unitPrice)} / campaign`}
            />
          ) : g.key === "priceProcess" ? (
            <SummaryRow
              indent
              muted
              name="Unit price"
              right={`${formatCurrency(g.unitPrice)} / process`}
            />
          ) : g.key === "sms" ? (
            <SummaryRow
              indent
              muted
              name="SMS sent"
              right={formatNumber(g.smsSent)}
            />
          ) : g.key === "photoService" ? (
            <SummaryRow
              indent
              muted
              name="Photos edited"
              right={formatNumber(g.photosEdited)}
            />
          ) : g.key === "editorReview" ? (
            <SummaryRow
              indent
              muted
              name="Reviews"
              right={formatNumber(g.reviews)}
            />
          ) : g.key === "fruitBox" ? (
            <SummaryRow
              indent
              muted
              name="Boxes"
              right={formatNumber(g.boxes)}
            />
          ) : g.key === "photo" ? (
            <>
              <SummaryRow
                indent
                muted
                name="Credits purchased"
                right={formatNumber(g.creditsPurchased)}
              />
              <SummaryRow
                indent
                muted
                name="Credits used"
                right={formatNumber(g.creditsUsed)}
              />
              <SummaryRow
                indent
                muted
                name="Credits remaining"
                right={formatNumber(g.creditsRemaining)}
              />
            </>
          ) : null}
        </div>
      ))}
      <TotalLine
        label="Total"
        sub="(inc. GST)"
        amount={formatCurrency(data.totalIncGst)}
        subAmount={formatCurrency(data.gstIncluded)}
      />
    </SummaryWrap>
  );
};

const OverallSummary = ({ data }) => {
  if (!data) return null;
  return (
    <SummaryWrap label="Summary">
      {(data.streams || []).map((s) => (
        <SummaryRow
          key={s.name}
          dot={s.dot}
          name={s.name}
          meta={s.meta}
          right={<Money value={s.amount} />}
        />
      ))}
      <TotalLine
        label="Total Revenue"
        sub="(inc. GST)"
        amount={formatCurrency(data.totalIncGst)}
        subAmount={formatCurrency(data.gstIncluded)}
      />
    </SummaryWrap>
  );
};

/* -------------------------------------------------------------------------- */
/*  Ledger column definitions                                                 */
/* -------------------------------------------------------------------------- */

const subscriptionColumns = [
  {
    accessorKey: "settlementDate",
    header: "Settlement Date",
    cell: (row) => formatDate(row.settlementDate),
    exportValue: (row) => formatDate(row.settlementDate),
    minWidth: "120px",
  },
  {
    accessorKey: "index",
    header: "#",
    cell: (_, i, page) => (page - 1) * 15 + i + 1,
    isFilter: false,
    isSortable: false,
    minWidth: "44px",
  },
  {
    accessorKey: "mainAgent",
    header: "Main Agent",
    cell: (row) => (
      <span className="font-medium text-black">{dash(row.mainAgent)}</span>
    ),
    minWidth: "150px",
  },
  {
    accessorKey: "name",
    header: "Plan",
    cell: (row) => <CategoryPill value={row.category} />,
    minWidth: "110px",
  },
  {
    accessorKey: "companyName",
    header: "Company",
    cell: (row) => dash(row.companyName),
    minWidth: "140px",
    maxWidth: "180px",
    wrap: true,
  },
  {
    accessorKey: "coAgents",
    header: "Co-Agents",
    cell: (row) => {
      const list = row.coAgents || [];
      if (!list.length) return <span className="text-darkgray">—</span>;
      const shown = list.slice(0, 2);
      return (
        <span className="text-darkergray text-xs">
          {shown.join(", ")}
          {list.length > 2 ? ` +${list.length - 2}` : ""}
        </span>
      );
    },
    exportValue: (row) => (row.coAgents || []).join("; "),
    isFilter: false,
    minWidth: "150px",
    maxWidth: "200px",
    wrap: true,
  },
  {
    accessorKey: "statusLive",
    header: "Status",
    cell: (row) =>
      row.statusLive ? <StatusPill value={row.statusLive} /> : "—",
    isFilter: false,
    minWidth: "100px",
  },
  {
    accessorKey: "seats",
    header: "Seats",
    cell: (row) => (row.seats != null ? formatNumber(row.seats) : "—"),
    isFilter: false,
    minWidth: "70px",
  },
  {
    accessorKey: "seatPrice",
    header: "Seat Price",
    cell: (row) =>
      row.seatPrice != null ? formatCurrency(row.seatPrice) : "—",
    isFilter: false,
    minWidth: "110px",
  },
  {
    accessorKey: "seatTotal",
    header: "Seat Total",
    cell: (row) => (row.seatTotal ? formatCurrency(row.seatTotal) : "—"),
    isFilter: false,
    minWidth: "110px",
  },
  {
    accessorKey: "qtySocial",
    header: "Qty Social",
    cell: (row) => (row.qtySocial ? formatNumber(row.qtySocial) : "—"),
    isFilter: false,
    minWidth: "90px",
  },
  {
    accessorKey: "socialMedia",
    header: "Social Media",
    cell: (row) =>
      row.socialMedia != null ? formatCurrency(row.socialMedia) : "—",
    isFilter: false,
    minWidth: "120px",
  },
  {
    accessorKey: "socialTotal",
    header: "Social Total",
    cell: (row) => (row.socialTotal ? formatCurrency(row.socialTotal) : "—"),
    isFilter: false,
    minWidth: "110px",
  },
  {
    accessorKey: "monthlyTotal",
    header: "Monthly Total",
    cell: (row) => <Money value={row.monthlyTotal ?? row.amount} bold />,
    isFilter: false,
    minWidth: "130px",
  },
  {
    accessorKey: "periodStart",
    header: "Period Start",
    cell: (row) => formatDate(row.periodStart),
    exportValue: (row) => formatDate(row.periodStart),
    isFilter: false,
    minWidth: "120px",
  },
  {
    accessorKey: "periodEnd",
    header: "Period End",
    cell: (row) => formatDate(row.periodEnd),
    exportValue: (row) => formatDate(row.periodEnd),
    isFilter: false,
    minWidth: "120px",
  },
  {
    accessorKey: "billingEmail",
    header: "Billing Email",
    cell: (row) => dash(row.billingEmail),
    minWidth: "180px",
    maxWidth: "220px",
    wrap: true,
  },
];

const SubscriptionExpanded = ({ row }) => {
  if (row.type !== "company") return null;
  const items = row.lineItems || [];
  if (items.length === 0) return null;
  return (
    <div className="flex flex-col gap-2 max-w-xl">
      {items.map((li, i) => (
        <div
          key={i}
          className="flex items-center justify-between bg-white border border-mediumgray rounded-md px-3 py-2"
        >
          <div className="flex flex-col">
            <span className="font-medium text-black text-sm">
              {dash(li.description)}
            </span>
            {Array.isArray(li.agentNames) && li.agentNames.length > 0 && (
              <span className="text-xs text-darkgray">
                {li.agentNames.join(", ")}
              </span>
            )}
          </div>
          <Money value={li.amount} />
        </div>
      ))}
    </div>
  );
};

// Expanded view for a Fruit Box ledger row — lists every property (address,
// qty, amount) consolidated into that month's company charge.
const FruitBoxExpanded = ({ row }) => {
  const items = row.lineItems || [];
  if (items.length === 0) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
      {items.map((li, i) => (
        <div
          key={i}
          className="flex items-center justify-between bg-white border border-mediumgray rounded-md px-3 py-2"
        >
          <div className="flex flex-col">
            <span className="font-medium text-black text-sm">
              {dash(li.address)}
            </span>
            {li.quantity > 1 && (
              <span className="text-xs text-darkgray">Qty: {li.quantity}</span>
            )}
          </div>
          <Money value={li.amount} />
        </div>
      ))}
    </div>
  );
};

const serviceColumns = [
  {
    accessorKey: "paymentOption",
    header: "Method",
    cell: (row) => <MethodPill value={row.paymentOption} />,
    exportValue: (row) => row.paymentOption || "",
    minWidth: "110px",
  },
  {
    accessorKey: "settlementDate",
    header: "Settlement Date",
    cell: (row) => formatDate(row.settlementDate),
    exportValue: (row) => formatDate(row.settlementDate),
    minWidth: "130px",
  },
  {
    accessorKey: "userName",
    header: "Agent",
    cell: (row) => (
      <span className="font-medium text-black">{dash(row.userName)}</span>
    ),
    minWidth: "150px",
  },
  {
    accessorKey: "name",
    header: "Product",
    cell: (row) => <span className="text-black">{dash(row.name)}</span>,
    minWidth: "170px",
  },
  {
    accessorKey: "category",
    header: "Category",
    cell: (row) => <CategoryPill value={row.category} />,
    minWidth: "110px",
  },
  {
    accessorKey: "quantity",
    header: "Qty",
    cell: (row) => formatNumber(row.quantity),
    isFilter: false,
    minWidth: "80px",
  },
  {
    accessorKey: "address",
    header: "Property Address",
    cell: (row) => dash(row.address),
    maxWidth: "220px",
  },
  {
    accessorKey: "amount",
    header: "Service Total",
    cell: (row) => <Money value={row.amount} bold />,
    isFilter: false,
    minWidth: "120px",
  },
  {
    accessorKey: "commission",
    header: "Commission (4.4%)",
    cell: (row) => (
      <span className="font-semibold text-green-700 tabular-nums">
        {formatCurrency(row.commission)}
      </span>
    ),
    isFilter: false,
    minWidth: "140px",
  },
];

const otherColumns = [
  {
    accessorKey: "settlementDate",
    header: "Settlement Date",
    cell: (row) => formatDate(row.settlementDate),
    exportValue: (row) => formatDate(row.settlementDate),
    minWidth: "130px",
  },
  {
    accessorKey: "userName",
    header: "Agent",
    cell: (row) => (
      <span className="font-medium text-black">
        {dash(row.userName || row.companyName)}
      </span>
    ),
    minWidth: "150px",
  },
  {
    accessorKey: "name",
    header: "Product",
    cell: (row) => <span className="text-black">{dash(row.name)}</span>,
    minWidth: "180px",
  },
  {
    accessorKey: "category",
    header: "Type",
    cell: (row) => <CategoryPill value={row.category} />,
    minWidth: "120px",
  },
  {
    accessorKey: "address",
    header: "Property Address",
    cell: (row) => dash(row.address),
    maxWidth: "240px",
  },
  {
    accessorKey: "quantity",
    header: "Qty",
    cell: (row) => formatNumber(row.quantity),
    isFilter: false,
    minWidth: "80px",
  },
  {
    accessorKey: "amount",
    header: "Amount",
    cell: (row) => <Money value={row.amount} bold />,
    isFilter: false,
    minWidth: "120px",
  },
];

const campaignFeeColumns = [
  {
    accessorKey: "settlementDate",
    header: "Settlement Date",
    cell: (row) => formatDate(row.settlementDate),
    exportValue: (row) => formatDate(row.settlementDate),
    minWidth: "130px",
  },
  {
    accessorKey: "userName",
    header: "Agent",
    cell: (row) => (
      <span className="font-medium text-black">{dash(row.userName)}</span>
    ),
    minWidth: "150px",
  },
  {
    accessorKey: "soldDate",
    header: "Sold Date",
    cell: (row) => formatDate(row.soldDate),
    exportValue: (row) => formatDate(row.soldDate),
    isFilter: false,
    minWidth: "130px",
  },
  {
    accessorKey: "liveDate",
    header: "Live Date",
    cell: (row) => formatDate(row.liveDate),
    exportValue: (row) => formatDate(row.liveDate),
    isFilter: false,
    minWidth: "130px",
  },
  {
    accessorKey: "address",
    header: "Property Address",
    cell: (row) => dash(row.address),
    maxWidth: "240px",
  },
  {
    accessorKey: "basePrice",
    header: "Base Price",
    cell: (row) => formatCurrency(row.basePrice),
    isFilter: false,
    minWidth: "120px",
  },
  {
    accessorKey: "gst",
    header: "GST",
    cell: (row) => (
      <span className="text-darkgray tabular-nums">
        {formatCurrency(row.gst)}
      </span>
    ),
    isFilter: false,
    minWidth: "100px",
  },
  {
    accessorKey: "amount",
    header: "Total Inc. GST",
    cell: (row) => <Money value={row.amount} bold />,
    isFilter: false,
    minWidth: "140px",
  },
  {
    accessorKey: "failed",
    header: "Charge",
    cell: (row) =>
      row.failed ? (
        <span className="text-red-600 text-xs font-medium">
          Failed · not counted
        </span>
      ) : (
        dash(null)
      ),
    exportValue: (row) => (row.failed ? "Failed" : ""),
    isFilter: false,
    minWidth: "150px",
  },
];

/* -------------------------------------------------------------------------- */
/*  Expandable revenue card                                                   */
/* -------------------------------------------------------------------------- */

const RevenueCard = ({ label, value, sub, open, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`text-left bg-white rounded-lg border p-4 transition-colors ${
      open ? "border-black" : "border-mediumgray hover:border-darkgray"
    }`}
  >
    <div className="flex items-start justify-between">
      <p className="text-sm text-darkgray mb-1">{label}</p>
      {open ? (
        <ChevronDown size={16} className="text-darkgray" />
      ) : (
        <ChevronRight size={16} className="text-darkgray" />
      )}
    </div>
    <p className="text-2xl font-bold text-black">{value}</p>
    {sub && <p className="text-xs text-darkgray mt-2">{sub}</p>}
  </button>
);

const LedgerCard = ({
  title,
  subtitle,
  columns,
  data,
  exportFilename,
  expandedRow,
  isRowExpandable,
}) => (
  <div className="bg-white rounded-lg border border-mediumgray">
    <div className="flex items-center justify-between border-b border-mediumgray px-4 py-3">
      <div>
        <h5 className="font-semibold text-black">{title}</h5>
        {subtitle && <p className="text-xs text-darkgray mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="p-4">
      <Table
        columns={columns}
        data={data}
        pageSize={15}
        emptyText="No records found"
        exportable
        exportFilename={exportFilename}
        renderExpanded={expandedRow}
        isRowExpandable={isRowExpandable}
      />
    </div>
  </div>
);

/* -------------------------------------------------------------------------- */
/*  FinancialDashboard                                                        */
/* -------------------------------------------------------------------------- */

const TABS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "payments", label: "Payments" },
];

const TabButton = ({ tab, isActive, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(tab.id)}
    role="tab"
    aria-selected={isActive}
    className={`whitespace-nowrap py-2 px-2.5 border-b-2 font-medium text-sm transition-colors ${
      isActive
        ? "border-black text-black"
        : "border-transparent text-darkgray hover:text-darkergray hover:border-lightgray"
    }`}
  >
    {tab.label}
  </button>
);

const FinancialDashboard = () => {
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [openCard, setOpenCard] = useState(null); // 'subscription' | 'campaignFee' | 'service' | 'other' | 'overall'

  const [filters, setFilters] = useState({
    period: "",
    startDate: "",
    endDate: "",
    companyId: "",
    userId: "",
  });

  const {
    data: financialData,
    isPending: isLoadingFinancials,
    isFetching,
  } = useGetFinancialsQuery(filters);
  const { data: companiesData } = useGetCompaniesQuery();
  const { data: usersData } = useGetUserQuery({
    companyId: filters.companyId,
    isExempt: { $ne: true },
    role: "user",
    isVisible: true,
  });

  const isLoading = isFetching || isLoadingFinancials;

  // The figures are cached on the server for five minutes; this builds them now.
  const refreshFinancials = useRefreshFinancials();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshFinancials();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleFilterChange = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));
  const handleDateChange = ({ startDate, endDate }) =>
    setFilters((prev) => ({
      ...prev,
      startDate,
      endDate,
      period: startDate && endDate ? "custom" : "",
    }));
  const clearFilters = () =>
    setFilters({
      period: "",
      startDate: "",
      endDate: "",
      companyId: "",
      userId: "",
    });

  const service = financialData?.service || [];
  const subscription = financialData?.subscription || [];
  const campaignFee = financialData?.campaignFee || [];
  const other = financialData?.other || [];
  const totals = financialData?.totals || {};
  const breakdown = financialData?.breakdown || {};
  const cards = breakdown.cards || {};

  const toggle = (key) => setOpenCard((cur) => (cur === key ? null : key));

  const handleExportAll = () => {
    const workbook = XLSX.utils.book_new();
    const sheets = [
      {
        name: "Subscription",
        columns: subscriptionColumns,
        data: subscription,
      },
      { name: "Service Revenue", columns: serviceColumns, data: service },
      { name: "Campaign Fee", columns: campaignFeeColumns, data: campaignFee },
      { name: "Other Revenue", columns: otherColumns, data: other },
    ];
    for (const s of sheets) {
      const rows = buildExportRows(s.columns, s.data);
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(workbook, ws, s.name);
    }
    XLSX.writeFile(workbook, "financial-dashboard.xlsx");
  };

  return (
    <div className="min-h-screen pb-10">
      <div className="container">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h4 className="k mb-2">Financial Dashboard</h4>
            <p className="text-darkgray">
              Track subscriptions, listing, campaign fees, and commission revenue
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-4 py-2.5 border border-mediumgray bg-white rounded-md text-sm text-darkergray hover:bg-lightgray hover:text-black transition-colors whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw size={16} className={isRefreshing ? "animate-spin" : ""} />
              Refresh
            </button>
            {activeTab === "dashboard" && (
              <button
                type="button"
                onClick={handleExportAll}
                className="flex items-center gap-2 px-4 py-2.5 border border-mediumgray bg-white rounded-md text-sm text-darkergray hover:bg-lightgray hover:text-black transition-colors whitespace-nowrap"
              >
                <Download size={16} />
                Export All (.xlsx)
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-lightgray mb-6">
          <nav className="-mb-px flex overflow-auto" role="tablist">
            {TABS.map((tab) => (
              <TabButton
                key={tab.id}
                tab={tab}
                isActive={activeTab === tab.id}
                onClick={setActiveTab}
              />
            ))}
          </nav>
        </div>

        {activeTab === "payments" && <Payments />}

        {activeTab === "dashboard" && (
          <>
            {/* Filters */}
            <Filter
              filters={filters}
              companiesData={companiesData}
              usersData={usersData}
              isLoading={isLoading}
              onFilterChange={handleFilterChange}
              onDateChange={handleDateChange}
              onClear={clearFilters}
            />

            {/* Revenue cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <RevenueCard
                label="Subscription Revenue"
                value={formatCurrency(
                  cards.subscription?.total ?? totals.subscriptionTotal,
                )}
                sub={`${cards.subscription?.count ?? subscription.length} subscriptions`}
                open={openCard === "subscription"}
                onToggle={() => toggle("subscription")}
              />
              <RevenueCard
                label="Campaign Fee Revenue"
                value={formatCurrency(
                  cards.campaignFee?.total ?? totals.campaignFeeTotal,
                )}
                sub={`${cards.campaignFee?.count ?? campaignFee.length} campaign fees`}
                open={openCard === "campaignFee"}
                onToggle={() => toggle("campaignFee")}
              />
              <RevenueCard
                label="Service Revenue"
                value={formatCurrency(
                  cards.service?.total ?? totals.beleefTotal,
                )}
                sub={
                  cards.service
                    ? `${Math.round((cards.service.rate || 0) * 100)}% on total services ${formatCurrency(
                        cards.service.totalServices,
                      )}`
                    : "commission"
                }
                open={openCard === "service"}
                onToggle={() => toggle("service")}
              />
              <RevenueCard
                label="Other Revenue"
                value={formatCurrency(cards.other?.total ?? totals.otherTotal)}
                sub={`${cards.other?.count ?? other.length} purchases`}
                open={openCard === "other"}
                onToggle={() => toggle("other")}
              />
              <RevenueCard
                label="Overall Total"
                value={formatCurrency(
                  cards.overall?.total ?? totals.overallTotal,
                )}
                sub="all revenue streams"
                open={openCard === "overall"}
                onToggle={() => toggle("overall")}
              />
            </div>

            {/* Expanded content */}
            <div className="relative">
              {isLoading && (
                <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10 rounded-lg">
                  <Loader2 className="w-8 h-8 text-black animate-spin" />
                </div>
              )}

              {openCard === "subscription" && (
                <>
                  <SubscriptionSummary data={breakdown.subscription} />
                  <LedgerCard
                    title="Subscription Ledger"
                    subtitle={`Sorted by charged date · ${formatNumber(subscription.length)} records`}
                    columns={subscriptionColumns}
                    data={subscription}
                    exportFilename="subscription-ledger"
                    expandedRow={(row) => <SubscriptionExpanded row={row} />}
                    isRowExpandable={(row) =>
                      row.type === "company" &&
                      Array.isArray(row.lineItems) &&
                      row.lineItems.length > 0
                    }
                  />
                </>
              )}

              {openCard === "campaignFee" && (
                <>
                  <CampaignFeeSummary data={breakdown.campaignFee} />
                  <LedgerCard
                    title="Campaign Fee Ledger"
                    subtitle={`Campaign Fees · Sorted by charged date · ${formatNumber(campaignFee.length)} records`}
                    columns={campaignFeeColumns}
                    data={campaignFee}
                    exportFilename="campaign-fee-ledger"
                  />
                </>
              )}

              {openCard === "service" && (
                <>
                  <ServiceSummary data={breakdown.service} />
                  <LedgerCard
                    title="Service Revenue Ledger"
                    subtitle={`${parseFloat(
                      ((breakdown.service?.rate || 0) * 100).toFixed(1),
                    )}% commission on all services · ${formatNumber(service.length)} records · Total commission: ${formatCurrency(
                      totals.beleefTotal,
                    )}`}
                    columns={serviceColumns}
                    data={service}
                    exportFilename="service-revenue-ledger"
                  />
                </>
              )}

              {openCard === "other" && (
                <>
                  <OtherSummary data={breakdown.other} />
                  <LedgerCard
                    title="Other Revenue Ledger"
                    subtitle={`SMS, AI Photo Edit, Photo Edit Service, Editor Review, Campaign Add-Ons, Price Process, Shop, Fruit Box · ${formatNumber(other.length)} records`}
                    columns={otherColumns}
                    data={other}
                    exportFilename="other-revenue-ledger"
                    expandedRow={(row) => <FruitBoxExpanded row={row} />}
                    isRowExpandable={(row) =>
                      Array.isArray(row.lineItems) && row.lineItems.length > 0
                    }
                  />
                </>
              )}

              {openCard === "overall" && (
                <OverallSummary data={breakdown.overall} />
              )}

              {!openCard && (
                <p className="text-center text-sm text-darkgray py-10">
                  Select a card above to see its breakdown and ledger.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FinancialDashboard;
